import type { FC } from "react";
import { Text } from "@/components/podkit/typography/Text";
import { AutomationStepKindPill } from "@/components/automations/AutomationStepKindPill";
import { IconPlayCircle } from "central-icons/IconPlayCircle";
import { AutomationStepNode } from "@/components/automations/AutomationStepNode";
import { pluralizeWithCount } from "@/utils/strings";
import { Pill } from "@/components/podkit/status/Pill";
import { AutomationStepArrow } from "@/components/automations/AutomationStepArrow";
import {
    WorkflowEditFormTriggerContextType,
    type WorkflowEditFormTriggerData,
    type WorkflowEditFormLimitsData,
} from "@/components/automations/automation-edit-form-data";
import { cn, type PropsWithClassName } from "@/components/podkit/lib/cn";
import type { PlainMessage } from "@bufbuild/protobuf";
import { Principal, type Subject } from "gitpod-next-api/gitpod/v1/identity_pb";
import { useMembers } from "@/queries/organization-queries";
import { Avatar, AvatarInitials, AvatarFallback, AvatarImage } from "@/components/Avatar";
import { useListServiceAccounts } from "@/queries/service-account-queries";
import { ServiceAccountAvatar } from "@/components/settings/organization/service-accounts/ServiceAccountAvatar";
import { SkeletonBlock } from "@/components/podkit/loading/Skeleton";
import { formatCronToReadable } from "@/utils/cron";
import { pullRequestEventName } from "@/utils/automations";
import { ErrorMessage } from "@/components/ErrorMessage";

type AutomationTriggerNodeProps = {
    limits?: WorkflowEditFormLimitsData;
    trigger?: WorkflowEditFormTriggerData;
    executor?: PlainMessage<Subject>;
    selected?: boolean;
    onClick?: () => void;
};

export const AutomationTriggerNode: FC<AutomationTriggerNodeProps> = ({
    limits,
    trigger,
    executor,
    selected = false,
    onClick,
}) => {
    const getVariant = () => {
        if (selected) return "selected";
        if (onClick) return "editable";
        return "readOnly";
    };

    if (!trigger) {
        return (
            <li
                className="w-full"
                onClick={onClick}
                data-tracking-id="select-empty-automation-trigger-node-automation-step-list"
            >
                <AutomationStepNode variant={getVariant()} className="px-4 py-3.5">
                    <div className="flex flex-1 flex-col items-start gap-1.5">
                        <AutomationStepKindPill className="mb-2" variant="default" LeadingIcon={IconPlayCircle}>
                            Trigger
                        </AutomationStepKindPill>
                    </div>
                </AutomationStepNode>
            </li>
        );
    }

    let triggerTypeLabel: string;
    switch (trigger.type) {
        case "manual":
            triggerTypeLabel = "Manual";
            break;
        case "time": {
            const cronExpression = trigger.config?.time?.cronExpression;
            if (cronExpression) {
                triggerTypeLabel = formatCronToReadable(cronExpression) ?? "Scheduled";
            } else {
                triggerTypeLabel = "Scheduled";
            }
            break;
        }
        case "pullRequest":
            triggerTypeLabel = "Pull Request";
            break;
        default:
            triggerTypeLabel = "Unknown";
            break;
    }

    return (
        <li className="w-full" onClick={onClick} data-tracking-id="select-automation-trigger-node-automation-step-list">
            <AutomationStepNode
                variant={getVariant()}
                className={cn("py-3.5 pl-4 pr-4", { "cursor-pointer": !!onClick && !selected })}
            >
                <div className="flex flex-1 flex-col items-start gap-2">
                    <AutomationStepKindPill variant="default" LeadingIcon={IconPlayCircle}>
                        <span>Trigger</span>
                    </AutomationStepKindPill>
                    <Text className="text-medium font-medium">{triggerTypeLabel}</Text>
                    <TriggerContextLabel context={trigger.context} />
                    <TriggerPullRequestEventsLabel trigger={trigger} />
                    <TriggerLimitsLabel limits={limits} />
                    <ExecutorLabel executor={executor} className="pt-1" />
                </div>
            </AutomationStepNode>
            <AutomationStepArrow className="-mt-[5.5px] ml-6" />
        </li>
    );
};

const TriggerContextLabel: FC<{ context: WorkflowEditFormTriggerData["context"] }> = ({ context }) => {
    if (!context) {
        return null;
    }

    switch (context.type) {
        case WorkflowEditFormTriggerContextType.Projects:
            return (
                <Text className="text-base">
                    <span>Runs across </span>
                    <Pill variant="default">{pluralizeWithCount(context.projectIds.length, false, "project")}</Pill>
                </Text>
            );
        case WorkflowEditFormTriggerContextType.RepositoriesList:
            return (
                <Text className="text-base">
                    <span>Runs across </span>
                    <Pill variant="default">
                        {pluralizeWithCount(context.repoUrls.length, false, "repository", "repositories")}
                    </Pill>
                </Text>
            );
        case WorkflowEditFormTriggerContextType.RepositoriesSearch:
            return (
                <Text className="text-base">
                    <span>Runs for repositories matching </span>
                    {context.repoSearchString ? (
                        <Pill variant="default">{context.repoSearchString}</Pill>
                    ) : (
                        <span className="font-mono text-content-muted">-</span>
                    )}
                </Text>
            );
        default:
            return null;
    }
};

const TriggerPullRequestEventsLabel: FC<{ trigger: WorkflowEditFormTriggerData }> = ({ trigger }) => {
    if (trigger.type !== "pullRequest") {
        return null;
    }

    const events = trigger.config?.pullRequest?.events;
    if (!events || events.length === 0) {
        return null;
    }

    return (
        <Text className="text-base">
            <span>Triggers when a PR is: </span>
            <span className="inline-flex flex-wrap gap-1">
                {events.map((event) => (
                    <Pill key={event} variant="default" className="font-mono">
                        {pullRequestEventName(event)}
                    </Pill>
                ))}
            </span>
        </Text>
    );
};

const TriggerLimitsLabel: FC<{ limits?: WorkflowEditFormLimitsData }> = ({ limits }) => {
    if (!limits) {
        return null;
    }

    const maxParallel = limits.maxParallel ?? 10;
    const maxTotal = limits.maxTotal ?? 100;

    return (
        <Text className="text-base">
            <span className="font-medium">{maxParallel} concurrent</span>
            <span> and </span>
            <span className="font-medium">
                <span>Max </span>
                {pluralizeWithCount(maxTotal, false, "action")}
            </span>
            <span> per batch</span>
        </Text>
    );
};

const ExecutorLabel: FC<PropsWithClassName<{ executor?: PlainMessage<Subject> }>> = ({ executor, className }) => {
    const {
        data: members,
        isLoading: isLoadingMembers,
        error: listMembersError,
    } = useMembers({
        enabled: executor?.principal === Principal.USER,
    });
    const {
        data: serviceAccountsData,
        isLoading: isLoadingServiceAccounts,
        error: listServiceAccountsError,
    } = useListServiceAccounts({
        enabled: executor?.principal === Principal.SERVICE_ACCOUNT,
    });

    if (!executor) {
        return null;
    }

    const error = listMembersError || listServiceAccountsError;
    if (error) {
        return <ErrorMessage error={error} />;
    }

    if (isLoadingMembers || isLoadingServiceAccounts) {
        return (
            <div className="flex items-center gap-1.5">
                <Text className="text-base">Runs as</Text>
                <SkeletonBlock ready={false} />
            </div>
        );
    }

    let name = "";
    let avatar: React.ReactNode = null;

    if (executor.principal === Principal.USER && members) {
        const executorMember = members.members.find((m) => m.userId === executor.id);
        if (executorMember) {
            name = executorMember.fullName;
            avatar = (
                <Avatar size={16}>
                    <AvatarImage src={executorMember.avatarUrl} alt={executorMember.fullName} />
                    <AvatarFallback>
                        <AvatarInitials name={executorMember.fullName} size={16} />
                    </AvatarFallback>
                </Avatar>
            );
        }
    } else if (executor.principal === Principal.SERVICE_ACCOUNT && serviceAccountsData) {
        const serviceAccount = serviceAccountsData.serviceAccounts.find((sa) => sa.id === executor.id);
        if (serviceAccount) {
            name = serviceAccount.name;
            avatar = <ServiceAccountAvatar id={serviceAccount.id} size={16} />;
        }
    }

    if (!name) {
        return null;
    }

    return (
        <Text className={cn("flex items-center gap-1.5 text-base text-content-strong", className)}>
            <span>Runs as</span>
            {avatar && <span>{avatar}</span>}
            <span>{name}</span>
        </Text>
    );
};
